import { defineStore } from 'pinia';
import { nextTick } from 'vue';
import { Config_Mode, Config_Pull_Stage, Config_Pull_Status } from '@/types/graphql';
import type {
  ClientView,
  CompareClientPackagesQuery,
  CompareClientPackagesQueryVariables,
  OffboardClientMutation,
  OffboardClientMutationVariables,
  GetClientQuery,
  GetClientsQuery,
  GetClientsQueryVariables,
  MoveClientMutation,
  MoveClientMutationVariables,
  PullConfigSubscription,
  PullConfigSubscriptionVariables,
  RejectClientCertificateMutation,
  RejectClientCertificateMutationVariables,
  SubClientUpdatedSubscription,
  SubClientUpdatedSubscriptionVariables,
  UpdateClientCertificateDto,
  UpdateClientCertificateMutation,
  UpdateClientCertificateMutationVariables,
  UpdateClientMutation,
  ResetClientCertificateMutation,
  ResetClientCertificateMutationVariables,
} from '@/types/graphql';

import getClients from '@/graphql/queries/getClients.graphql';
import getClient from '@/graphql/queries/getClient.graphql';
import moveClient from '@/graphql/mutations/moveClient.graphql';
import updateClient from '@/graphql/mutations/updateClient.graphql';
import offboardClient from '@/graphql/mutations/offboardClient.graphql';
import updateClientCertificate from '@/graphql/mutations/updateClientCertificate.graphql';
import rejectClientCertificate from '@/graphql/mutations/rejectClientCertificate.graphql';
import compareClientPackages from '@/graphql/queries/compareClientPackages.graphql';
import sendAction from '@/graphql/mutations/sendAction.graphql';
import resetClientCertificate from '@/graphql/mutations/resetClientCertificate.graphql';

import pullConfig from '@/graphql/subscriptions/pullConfig.graphql';
import subClientUpdated from '@/graphql/subscriptions/subClientUpdated.graphql';

import type { ToastMessageOptions } from 'primevue/toast';
import type { useToast } from 'primevue/usetoast';

import storeGraphqlQuery from '~/composables/storeGraphqlQuery';
import storeGraphqlMutation from '~/composables/storeGraphqlMutation';
import storeGraphqlSubscription from '~/composables/storeGraphqlSubscription';
import { useClientGroupsStore } from './client-groups';

// ---------- helpers ----------
const stripUndef = <T extends object>(o: T) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;

type PackageComparison = {
  configId: number;
  missingSoftwarePackages: string[];
  missingFirmwarePackages: string[];
};
// ---------- store ----------
export const useClientsStore = defineStore('clients', {
  state: () => ({
    clients: <Partial<ClientView>[]>[],
    loading: true,
    listLoading: true,
    detailLoading: false,
    pendingProfileAssignmentClientIds: [] as number[],
    queue: <
      Map<
        number,
        {
          status: Config_Pull_Status | 'PENDING' | 'ERROR';
          progress: number;
          stage?: Config_Pull_Stage;
          statusMessage?: string;
          message?: ToastMessageOptions;
          subscription: any;
        }
      >
      >new Map(),
    // per-client package comparison cache
    packageComparisons: {} as Record<number, PackageComparison | undefined>,
    lastSeenHashes: {} as Record<number, string | undefined>,
    comparisonVersions: {} as Record<number, number>,
    clientsSubscription: null as any,
    getClientsInFlight: null as Promise<void> | null,
  }),
  actions: {

    // -------- package comparison cache helpers --------
    invalidatePackageComparison(clientId: number) {
      delete this.packageComparisons[clientId];
    },
    async fetchPackageComparison(clientId: number, configId: number) {
      const cached = this.packageComparisons[clientId];
      if (cached && cached.configId === configId) return cached;

      try {
        const res = await storeGraphqlQuery<
          CompareClientPackagesQuery,
          CompareClientPackagesQueryVariables
        >(
          { query: compareClientPackages, variables: { clientId, configId } },
          'Fehler beim Abrufen des Paketvergleichs',
        );

        const data = res?.data?.compareClientPackages;
        if (data) {
          // Replace the entire object to ensure reactivity
          this.packageComparisons = {
            ...this.packageComparisons,
            [clientId]: {
              configId,
              missingSoftwarePackages: [...data.missingSoftwarePackages],
              missingFirmwarePackages: [...data.missingFirmwarePackages],
            },
          };
        } else {
          console.warn('No package comparison for', { clientId, configId });
          this.packageComparisons = {
            ...this.packageComparisons,
            [clientId]: {
              configId,
              missingSoftwarePackages: [],
              missingFirmwarePackages: [],
            },
          };
        }
        return this.packageComparisons[clientId];
      } catch (error) {
        console.error('Error fetching package comparison:', error);
        this.packageComparisons = {
          ...this.packageComparisons,
          [clientId]: {
            configId,
            missingSoftwarePackages: [],
            missingFirmwarePackages: [],
          },
        };
        return this.packageComparisons[clientId];
      }
    },
    // -------- core data flow --------
    async getClients() {
      if (this.getClientsInFlight) {
        return this.getClientsInFlight;
      }

      this.getClientsInFlight = (async () => {
        this.loading = true;
        this.listLoading = true;

        // 1) initial snapshot
        const res = await storeGraphqlQuery<GetClientsQuery, GetClientsQueryVariables>(
          { query: getClients },
          'Fehler beim Laden der Clients',
        );

        // 2) subscribe to live updates (only once)
        if (!this.clientsSubscription) {
          const observable = await storeGraphqlSubscription<
            SubClientUpdatedSubscription,
            SubClientUpdatedSubscriptionVariables
          >({ query: subClientUpdated });

          if (observable) {
            this.clientsSubscription = observable.subscribe({
              complete: () => console.log('clients sub complete'),
              error: (error) => console.log('clients sub error', error),
              start: () => console.log('clients sub start'),
              next: async (payload) => {
                const subData = payload?.data?.subClientUpdated;
                if (!subData) return;

                if (subData.updated?.length) {
                  const groupStore = useClientGroupsStore();

                  subData.updated.forEach((updatedClient) => {
                    const idx = this.clients.findIndex(c => c.id === updatedClient.id);
                    const incoming = stripUndef(updatedClient) as Partial<ClientView>;

                    if (idx > -1) {
                      const before = this.clients[idx];
                      const incomingHash = incoming.configMd5Hash;
                      const incomingName = incoming.configurationName;
                      const lastSeenHash = this.lastSeenHashes[updatedClient.id];

                      const hashChanged = incomingHash && incomingHash !== lastSeenHash;
                      const nameChanged =
                        incomingName !== undefined && incomingName !== before.configurationName;

                      this.clients[idx] = { ...before, ...incoming };

                      if (incomingHash) {
                        this.lastSeenHashes[updatedClient.id] = incomingHash;
                      }

                      const currentClient = this.clients[idx];
                      const group = groupStore.groups?.find(g => g.id === currentClient.clientGroupId);
                      const configId = group?.config?.id;

                      if (configId && (hashChanged || nameChanged)) {
                        this.comparisonVersions[updatedClient.id] =
                          (this.comparisonVersions[updatedClient.id] ?? 0) + 1;
                        this.invalidatePackageComparison(updatedClient.id);
                        this.fetchPackageComparison(updatedClient.id, configId);
                      } else if (configId && !this.packageComparisons[updatedClient.id]) {
                        this.fetchPackageComparison(updatedClient.id, configId);
                      }
                    } else {
                      this.clients.push(incoming);
                      if (incoming.configMd5Hash) {
                        this.lastSeenHashes[incoming.id!] = incoming.configMd5Hash;
                      }
                    }
                  });

                  this.clients = [...this.clients];
                  this.lastSeenHashes = { ...this.lastSeenHashes };
                  this.comparisonVersions = { ...this.comparisonVersions };
                  await nextTick();
                }

              // CREATED
                if (subData.created?.length) {
                  subData.created.forEach((createdClient) => {
                    const exists = this.clients.some(c => c.id === createdClient.id);
                    if (exists) {
                      const idx = this.clients.findIndex(c => c.id === createdClient.id);
                      this.clients[idx] = { ...this.clients[idx], ...stripUndef(createdClient as any) };
                    } else {
                      this.clients.push(createdClient as ClientView);
                    }
                  });
                  this.clients = [...this.clients];
                }

                // DELETED
                if (subData.deleted?.length) {
                  subData.deleted.forEach((deletedId) => {
                    const idx = this.clients.findIndex(c => c.id === deletedId);
                    if (idx > -1) this.clients.splice(idx, 1);
                  });
                  this.clients = [...this.clients];
                }
              }

            });
          }
        }

        // 3) merge snapshot into anything we might already have (don’t overwrite live updates)
        if (res?.data) {
          const byId = new Map<number, Partial<ClientView>>();
          this.clients.forEach(c => byId.set(c.id!, c));
          res.data.getClients.forEach((c) => {
            const prev = byId.get(c.id!) ?? {};
            byId.set(c.id!, { ...prev, ...c as Partial<ClientView> });
            if (c.configMd5Hash) {
              this.lastSeenHashes[c.id!] = c.configMd5Hash;
            }
          });
          this.clients = [...byId.values()];
        }

        this.loading = false;
        this.listLoading = false;
      })();

      try {
        return await this.getClientsInFlight;
      } finally {
        this.getClientsInFlight = null;
      }
    },

    async moveClient(clientId: number, groupId: number) {
      this.loading = true;
      this.listLoading = true;
      const res = await storeGraphqlMutation<MoveClientMutation, MoveClientMutationVariables>(
        { mutation: moveClient, variables: { clientId, groupId } },
        'Fehler beim Verschieben des Clients',
      );
      await this.getClients();
      this.loading = false;
      this.listLoading = false;
      return res;
    },

    async updateClient(client: {
      clientId: number;
      description?: string | null;
      configType: 'production' | 'staging' | 'ignored' | 'purge';
    }) {
      this.loading = true;
      this.listLoading = true;
      const res = await storeGraphqlMutation<UpdateClientMutation, any>(
        { mutation: updateClient, variables: { ...client } },
        'Fehler beim Aktualisieren des Clients',
      );
      await this.getClients();
      this.loading = false;
      this.listLoading = false;
      return res;
    },

    async getClientbyId(id: number) {
      this.detailLoading = true;
      try {
        const res = await storeGraphqlQuery<GetClientQuery>(
          { query: getClient, variables: { id } },
          'Error while loading client',
        );
        this.detailLoading = false;
        return res;
      } catch (error) {
        this.detailLoading = false;
        throw error;
      }
    },

    /**
     * Re-fetches a single client by ID and merges the fresh data into
     * the clients array so reactive computed properties (e.g. config
     * status, hasAssignedProfiles) update without a full page reload.
     */
    async refreshClientInStore(id: number) {
      try {
        const res = await storeGraphqlQuery<GetClientQuery>(
          { query: getClient, variables: { id } },
          'Error while refreshing client',
        );
        const fresh = res?.data?.getClient;
        if (!fresh) return;

        const idx = this.clients.findIndex((c) => c.id === id);
        if (idx > -1) {
          this.clients[idx] = { ...this.clients[idx], ...(fresh as unknown as Partial<ClientView>) };
        } else {
          this.clients.push(fresh as unknown as Partial<ClientView>);
        }
        this.clients = [...this.clients];
        if (fresh.configMd5Hash) {
          this.lastSeenHashes[id] = fresh.configMd5Hash;
          this.lastSeenHashes = { ...this.lastSeenHashes };
        }
        // Invalidate package comparison so it is re-evaluated
        this.invalidatePackageComparison(id);
      } catch (error) {
        console.error('Error refreshing client in store:', error);
      }
    },

    async offboardClient(id: number) {
      this.loading = true;
      this.listLoading = true;
      const res = await storeGraphqlMutation<OffboardClientMutation, OffboardClientMutationVariables>(
        { mutation: offboardClient, variables: { id } },
        'Fehler beim Offboarding des Clients',
      );
      await this.getClients();
      this.loading = false;
      this.listLoading = false;
      return res;
    },

    async acceptClientCertificate(data: Required<UpdateClientCertificateDto>) {
      this.loading = true;
      this.listLoading = true;
      const res = await storeGraphqlMutation<
        UpdateClientCertificateMutation,
        UpdateClientCertificateMutationVariables
      >(
        { mutation: updateClientCertificate, variables: { certificateId: data.certificateId!, clientId: data.clientId } },
        'Fehler beim Update des Client-Zertifikats',
      );
      await this.getClients();
      this.loading = false;
      this.listLoading = false;
      return res;
    },

    async rejectClientCertificate(data: Required<UpdateClientCertificateDto>) {
      this.loading = true;
      this.listLoading = true;
      const res = await storeGraphqlMutation<
        RejectClientCertificateMutation,
        RejectClientCertificateMutationVariables
      >(
        { mutation: rejectClientCertificate, variables: { certificateId: data.certificateId!, clientId: data.clientId } },
        'Fehler beim Ablehnen des Client-Zertifikats',
      );
      await this.getClients();
      this.loading = false;
      this.listLoading = false;
      return res;
    },

    async resetCertificate(clientId:number) {
      this.loading = true;
      this.listLoading = true;

      const res = await  storeGraphqlMutation<
        ResetClientCertificateMutation,
        ResetClientCertificateMutationVariables
      >(
        {
          mutation: resetClientCertificate,
          variables: {
            clientId: clientId,
          }
        }
      );
      await this.getClients();
      this.loading = false;
      this.listLoading = false;
      return res;
    },

    async pullConfig(id: number, configMode: Config_Mode) {
      try {
        const nuxtApp = useNuxtApp();
        const getToast: typeof useToast = () => nuxtApp.vueApp.config.globalProperties.$toast;
        const toastService = getToast();

        const { resolveClient } = useApolloClient();
        const apolloClient = resolveClient('webSocketClient');
        const subRes = apolloClient.subscribe<PullConfigSubscription, PullConfigSubscriptionVariables>({
          query: pullConfig,
          variables: { id, configMode },
        });

        this.queue.set(id, {
          status: 'PENDING',
          progress: 3,
          stage: Config_Pull_Stage.ReceivingFile,
          statusMessage: 'config.toast.pull.steps.downloading',
          subscription: subRes.subscribe({
            complete: () => {
              console.log('pullConfig complete');
            },
            error: () => {
              const q = this.queue.get(id);
              if (!q) {
                toastService.add({ severity: 'error', summary: 'Fehler beim Laden der Config', life: 3000 });
                return;
              }
              q.status = 'ERROR';
            },
            next: (data) => {
              const q = this.queue.get(id);
              if (!q) return;
              if (data.errors) {
                console.error('Error while pulling config', data.errors);
                q.status = 'ERROR';
                return;
              }

              const pullUpdate = data.data?.subGetPullConfigStatus;
              if (!pullUpdate) return;

              q.stage = pullUpdate.stage;
              q.statusMessage = pullUpdate.message;

              if (typeof pullUpdate.progress === 'number') {
                const progress = pullUpdate.progress <= 1
                  ? pullUpdate.progress * 100
                  : pullUpdate.progress;
                q.progress = Math.min(100, Math.max(0, Math.round(progress)));
              }

              if (pullUpdate.status) {
                q.status = pullUpdate.status;
              }

              if (pullUpdate.status === Config_Pull_Status.Success) {
                q.progress = 100;
                q.statusMessage = 'config.toast.pull.steps.done';

                // Refresh the owning group so the context menu picks up
                // a freshly created staging (or production) config without
                // requiring a manual page reload.
                const client = this.clients.find((c) => c.id === id);
                if (client?.clientGroupId) {
                  const groupsStore = useClientGroupsStore();
                  groupsStore.refreshGroupInStore(client.clientGroupId).catch(() => {});
                }
              }
            },
            start: () => console.log('pullConfig start'),
          }),
        });
      } catch (error) {
        console.error('Error while pulling config', error);
      }
    },

    async sendWakeupAction(delegateId: number, macs: string[]) {
      try {
        return await storeGraphqlMutation<any, any>(
          {
            mutation: sendAction,
            variables: {
              id: delegateId,
              action: 'wakeup',
              message: JSON.stringify({ macs }),
            },
          },
          'Error sending wakeup action',
        );
      } catch (error) {
        console.error('Error sending wakeup action', error);
        throw error;
      }
    },

    setProfileAssignmentPending(clientIds: number[], pending: boolean) {
      const current = new Set(this.pendingProfileAssignmentClientIds);

      for (const clientId of clientIds) {
        if (pending) {
          current.add(clientId);
        } else {
          current.delete(clientId);
        }
      }

      this.pendingProfileAssignmentClientIds = [...current];
    },

    isProfileAssignmentPending(clientId: number): boolean {
      return this.pendingProfileAssignmentClientIds.includes(clientId);
    },
  },
});
